import React, { useState } from "react";
import {
  Product,
  TestCaseDesignElement,
  RequirementPolicies,
  TestCasePolicies,
  Policy,
} from "@design-controls/types";
import { QPageLoader } from "@qualio/ui-components";
import { Navigate } from "react-router";
import { createStore, StoreApi } from "zustand";

import { useSelectedProduct } from "../hooks/useSelectedProduct";

export type ProductContext = {
  product: Product;
  policy: Policy[];
  requirementModal: boolean;
  testCaseModal: boolean;
  riskModal: boolean;
  testResultModal?: TestCaseDesignElement;
  type?: RequirementPolicies["type"] | TestCasePolicies["type"];
  openRequirementModal: (type?: RequirementPolicies["type"]) => void;
  closeRequirementModal: () => void;
  openTestCaseModal: (type?: TestCasePolicies["type"]) => void;
  closeTestCaseModal: () => void;
  openRiskModal: () => void;
  closeRiskModal: () => void;
};

export const CurrentProductContext =
  React.createContext<StoreApi<ProductContext> | null>(null);

export const CurrentProductProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { isLoading, product, configs } = useSelectedProduct();

  if (!isLoading && !product) {
    return <Navigate to="/" />;
  }

  if (isLoading || !product || !configs) {
    return <QPageLoader />;
  }

  return (
    <ProductProviderContainer policy={configs} product={product}>
      {children}
    </ProductProviderContainer>
  );
};

type Props = {
  product: Product;
  policy: Policy[];
  children?: React.ReactNode;
};

const ProductProviderContainer: React.FC<Props> = ({
  policy,
  product,
  children,
}) => {
  const [store] = useState(() =>
    createStore<ProductContext>((set) => ({
      product,
      policy,
      requirementModal: false,
      testCaseModal: false,
      riskModal: false,
      type: undefined,
      openRequirementModal: (type?: RequirementPolicies["type"]) =>
        set({ requirementModal: true, type }),
      closeRequirementModal: () =>
        set({ requirementModal: false, type: undefined }),
      openTestCaseModal: (type?: TestCasePolicies["type"]) =>
        set({ testCaseModal: true, type }),
      closeTestCaseModal: () => set({ testCaseModal: false, type: undefined }),
      openRiskModal: () => set({ riskModal: true }),
      closeRiskModal: () => set({ riskModal: false }),
    })),
  );

  return (
    <CurrentProductContext.Provider value={store}>
      {children}
    </CurrentProductContext.Provider>
  );
};
